/**
 * Remark plugin for [[page link]] syntax.
 *
 * This is the main entry point for page link support in our markdown pipeline.
 * It combines the micromark tokenizer and mdast handler into a unified plugin.
 *
 * ## Usage
 *
 * ```ts
 * import { unified } from 'unified';
 * import remarkParse from 'remark-parse';
 * import { remarkPageLink } from './pageLinkSyntax';
 *
 * const processor = unified()
 *   .use(remarkParse)
 *   .use(remarkPageLink);
 * ```
 *
 * ## Syntax Reference
 *
 * | Markdown | Parsed Title |
 * |----------|--------------|
 * | `[[Page]]` | `Page` |
 * | `[[Page with spaces]]` | `Page with spaces` |
 * | `[[Page with \] bracket]]` | `Page with ] bracket` |
 * | `[[Page with \\ backslash]]` | `Page with \ backslash` |
 * | `[[Parent/Child]]` | `Parent/Child` (hierarchy handled separately) |
 *
 * ## Architecture
 *
 * This plugin is composed of two lower-level modules:
 *
 * - `micromark-page-link.ts`: Tokenizes [[...]] at the character level
 * - `mdast-page-link.ts`: Converts tokens to mdast nodes and handles escaping
 *
 * @see ./micromark-page-link.ts for tokenizer details
 * @see ./mdast-page-link.ts for mdast utilities
 * @module
 */
import { type Processor } from "unified";
import { CONTINUE, SKIP, visit } from "unist-util-visit";

import { pageLinkFromMarkdown, replaceEmbeddedPageLinksInMarkdownText } from "./mdast-page-link";
import { pageLinkSyntax } from "./micromark-page-link";
import { findPageLinkEnd } from "./pageLinkScan";

// Re-export utilities for use by other modules
export { escapePageTitleForMarkdown, unescapePageLinkTitle } from "./mdast-page-link";

/**
 * A remark plugin that adds [[Page Title]] syntax support.
 *
 * This plugin:
 * 1. Tokenizes [[...]] syntax using a micromark extension
 * 2. Converts tokens to HTML nodes with data attributes
 * 3. These HTML nodes are then processed by remarkProseMirror's htmlHandlers
 *
 * @example
 * ```ts
 * const processor = unified()
 *   .use(remarkParse)
 *   .use(remarkPageLink)
 *   .use(remarkProseMirror, { schema, htmlHandlers: { ... } });
 * ```
 */
export function remarkPageLink(this: Processor) {
	const data = this.data();

	const micromarkExtensions = data.micromarkExtensions || (data.micromarkExtensions = []);
	const fromMarkdownExtensions =
		data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);

	micromarkExtensions.push(pageLinkSyntax());

	fromMarkdownExtensions.push(pageLinkFromMarkdown() as any);

	return function (tree: any, file: { value?: unknown }) {
		const source = typeof file.value === "string" ? file.value : undefined;
		if (!source) {
			return;
		}

		visit(tree, "text", function (textNode: any, index, parent) {
			if (!parent || index === undefined || typeof textNode.value !== "string") {
				return CONTINUE;
			}

			if (!textNode.value.includes("![[")) {
				return CONTINUE;
			}

			const startOffset = textNode.position?.start?.offset;
			const endOffset = textNode.position?.end?.offset;
			let rawValue =
				typeof startOffset === "number" && typeof endOffset === "number"
					? source.slice(startOffset, endOffset)
					: textNode.value;
			if (
				typeof startOffset === "number" &&
				startOffset > 0 &&
				textNode.value.startsWith("!") &&
				source[startOffset - 1] === "\\"
			) {
				rawValue = `\\${rawValue}`;
			}

			const replacementNodes = replaceEmbeddedPageLinksInMarkdownText(
				textNode.value,
				rawValue
			).map((node) =>
				node.type === "html"
					? { type: "html", value: node.value }
					: { type: "text", value: node.value }
			);

			parent.children.splice(index, 1, ...replacementNodes);
			return SKIP;
		});

		visit(tree, "text", function (textNode: any) {
			if (typeof textNode.value !== "string" || !textNode.value.startsWith("[[")) {
				return CONTINUE;
			}

			const startOffset = textNode.position?.start?.offset;
			if (
				typeof startOffset === "number" &&
				startOffset >= 2 &&
				source[startOffset - 2] === "\\" &&
				source[startOffset - 1] === "!"
			) {
				textNode.value = `!${textNode.value}`;
			}

			return CONTINUE;
		});

		visit(tree, function (node: any) {
			if (!node || !("children" in node) || !Array.isArray(node.children)) {
				return CONTINUE;
			}

			for (let index = 0; index < node.children.length - 1; index += 1) {
				const currentChild = node.children[index];
				const nextChild = node.children[index + 1];

				if (
					currentChild?.type !== "text" ||
					typeof currentChild.value !== "string" ||
					!currentChild.value.endsWith("!") ||
					nextChild?.type !== "html" ||
					typeof nextChild.value !== "string" ||
					!nextChild.value.includes('data-page-link="true"')
				) {
					continue;
				}

				const textStartOffset = currentChild.position?.start?.offset;
				const textEndOffset = currentChild.position?.end?.offset;
				if (typeof textStartOffset !== "number" || typeof textEndOffset !== "number") {
					continue;
				}

				const rawTextSegment = source.slice(textStartOffset, textEndOffset);
				if (!rawTextSegment.endsWith("\\!")) {
					continue;
				}

				const rawPageLinkEnd = findPageLinkEnd(source, textEndOffset + 2, true);
				if (rawPageLinkEnd === -1) {
					continue;
				}

				const rawPageLinkSegment = source.slice(textEndOffset, rawPageLinkEnd);
				const mergedText = `${rawTextSegment.slice(0, -2)}!${rawPageLinkSegment}`;
				node.children.splice(index, 2, {
					type: "text",
					value: mergedText,
				});
			}

			return CONTINUE;
		});
	};
}
